import { z } from 'zod';
import type { PositionStaffingResult } from './PositionStaffingIntent';
import {
  normalizeOrgPersonFieldValue,
  orgPersonFieldValidationError,
} from '../fields/orgPersonWritableFields';

const common = {
  intentId: z.string().uuid(),
  expectedGraphVersion: z.number().int().nonnegative(),
  mode: z.enum(['live', 'scenario']).default('live'),
  effectiveAt: z.string().datetime().optional(),
  scenarioChangeSetId: z.string().min(1).optional(),
  scenarioId: z.string().uuid().optional(),
  expectedHeadChangeSetId: z.string().min(1).optional(),
  ownerDiagramId: z.string().min(1).optional(),
  scenarioName: z.string().trim().min(1).max(120).optional(),
};

const orgPersonWritableFieldsSchema = (options: { required: boolean }) => z.record(z.string(), z.string())
  .superRefine((fields, context) => {
    if (options.required && Object.keys(fields).length === 0) {
      context.addIssue({ code: 'custom', message: 'At least one person field is required.' });
    }
    for (const [fieldId, value] of Object.entries(fields)) {
      const message = orgPersonFieldValidationError(fieldId, value);
      if (message) context.addIssue({ code: 'custom', path: [fieldId], message });
    }
  })
  .transform((fields) => Object.fromEntries(
    Object.entries(fields).map(([fieldId, value]) => [fieldId, normalizeOrgPersonFieldValue(value)]),
  ));
const OrgPersonWritableFieldsSchema = orgPersonWritableFieldsSchema({ required: true });

export const PositionStructureIntentSchema = z.discriminatedUnion('kind', [
  z.object({
    ...common,
    kind: z.literal('create'),
    title: z.string().trim().min(1).max(160),
    department: z.string().trim().max(120).optional(),
    level: z.string().trim().max(80).optional(),
    managerPositionId: z.string().min(1).nullable().optional(),
    /**
     * A hire: create the position AND the person occupying it in one apply,
     * so people and position charts update together. Scenario-mode only —
     * the person fact needs the organization_plan change-set variant.
     */
    hire: z.object({
      name: z.string().trim().min(1).max(160),
      email: z.string().trim().max(320).optional(),
      /** Further writable Person fields set on the new card before submit. */
      fields: orgPersonWritableFieldsSchema({ required: false })
        .refine((fields) => !('name' in fields) && !('email' in fields), 'Name and email are set on the hire itself.')
        .optional(),
    }).optional(),
  }),
  z.object({ ...common, kind: z.literal('edit'), positionId: z.string().min(1), title: z.string().trim().min(1).max(160).optional(), department: z.string().trim().max(120).optional(), level: z.string().trim().max(80).optional() }),
  z.object({ ...common, kind: z.literal('reparent'), positionId: z.string().min(1), managerPositionId: z.string().min(1).nullable() }),
  z.object({ ...common, kind: z.literal('add_secondary_reporting'), positionId: z.string().min(1), managerPositionId: z.string().min(1), relationshipType: z.literal('matrix_reports') }),
  z.object({ ...common, kind: z.literal('add_seat'), sourcePositionId: z.string().min(1) }),
  z.object({ ...common, kind: z.literal('duplicate'), sourcePositionId: z.string().min(1), title: z.string().trim().min(1).max(160).optional() }),
  z.object({ ...common, kind: z.literal('retire'), positionId: z.string().min(1), reportingPolicy: z.enum(['retire_all', 'reparent_direct_reports']) }),
  /**
   * Removes a person record that holds no position — the Unassigned Employee
   * card an enacted transition leaves behind. There is no position to retire,
   * so the intent addresses the person directly.
   */
  z.object({ ...common, kind: z.literal('retire_person'), personId: z.string().min(1) }),
  /**
   * Updates canonical Person fields through the same organization-plan change
   * set as the canvas scenario. The server resolves the stable record to its
   * source key; clients never choose write-gate identity or bypass review.
   */
  z.object({
    ...common,
    kind: z.literal('update_person'),
    personId: z.string().min(1),
    fields: OrgPersonWritableFieldsSchema,
  }),
]).superRefine((intent, context) => {
  if (intent.kind === 'edit' && intent.title === undefined && intent.department === undefined && intent.level === undefined) {
    context.addIssue({ code: 'custom', message: 'At least one position field is required.' });
  }
  if (intent.mode === 'scenario' && !intent.effectiveAt) context.addIssue({ code: 'custom', path: ['effectiveAt'], message: 'Scenario authoring requires an effective date.' });
  if (intent.mode === 'live' && intent.scenarioChangeSetId) context.addIssue({ code: 'custom', path: ['scenarioChangeSetId'], message: 'Live authoring cannot target a scenario.' });
  if (intent.mode === 'scenario' && intent.scenarioChangeSetId
    && (!intent.scenarioId || !intent.expectedHeadChangeSetId)) {
    context.addIssue({ code: 'custom', path: ['scenarioId'], message: 'Scenario amendments require scenarioId and expectedHeadChangeSetId.' });
  }
});

export type PositionStructureIntent = z.infer<typeof PositionStructureIntentSchema>;
export type PositionStructureResult = PositionStaffingResult;
